import { useEffect, useState } from "react";
import { Box } from "@chakra-ui/react";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import { baseURL } from "../../../api/api";
import { PipelineImport } from "../../../api/types";
import notification from "../../../utils/notification";
import { typedSxMap } from "../../../utils/typedSxMap";
import style from "../../forms/style/Form.module.scss";

type RerunPipelineImportFormProps = {
  pipelineImport?: PipelineImport;
  onSuccess: () => void;
  onClose: () => void;
};

const styles = typedSxMap({
  actionButtons: {
    display: "flex",
    gap: "var(--m)",
    justifyContent: "flex-end",
    marginTop: "var(--m)",
  },
  metadataTextarea: {
    width: "100%",
    height: "200px",
    padding: "10px",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--border-radius-xs)",
    background: "var(--color-background-input)",
    color: "var(--color-text)",
    fontFamily: "monospace",
    marginTop: "10px",
    resize: "vertical",
  },
  metadataLabel: {
    color: "var(--color-text-soft)",
    marginTop: "var(--m)",
    fontSize: "14px",
  },
  fieldContainer: {
    marginBottom: "var(--m)",
  },
  schemaName: {
    color: "var(--color-text-soft)",
    fontSize: "14px",
    marginBottom: "var(--m)",
  },
});

export default function RerunPipelineImportForm({ pipelineImport, onSuccess, onClose }: RerunPipelineImportFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState("{}");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (pipelineImport) {
      // Generate the new name with incremented number
      setNewName(generateIncrementedName(pipelineImport.name));

      // Set metadata, removing the name property if it exists
      if (pipelineImport.metadata) {
        try {
          const metadataObj = { ...pipelineImport.metadata };
          // Remove the name from metadata to avoid duplication with the actual name field
          if (metadataObj.name) {
            delete metadataObj.name;
          }
          // Format the JSON for better readability
          const formatted = JSON.stringify(metadataObj, null, 2);
          setMetadata(formatted);
        } catch (error) {
          setMetadata("{}");
        }
      }
    }
  }, [pipelineImport]);

  const handleRerun = async () => {
    if (!pipelineImport) return;

    try {
      setIsLoading(true);

      // Download the original file
      const fileResponse = await fetch(`${baseURL}pipeline-import/${pipelineImport.id}/download-original`, {
        method: "GET",
        credentials: "include",
      });

      if (!fileResponse.ok) {
        const res = await fileResponse.json();
        throw res.error ? res.error : "Failed to download the original file";
      }

      const fileBlob = await fileResponse.blob();
      const contentType = fileResponse.headers.get("Content-Type") || "";
      const filename = pipelineImport.src_file_name || `${pipelineImport.id}${getExtensionFromContentType(contentType)}`;
      const file = new File([fileBlob], filename, { type: contentType });

      // Create form data for pipeline import upload
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("schema_id", pipelineImport.schema_id);

      // Use the name from the input field
      formData.append("name", newName);

      // Parse and validate metadata
      let parsedMetadata;
      try {
        parsedMetadata = JSON.parse(metadata);
        // Make sure name is not in the metadata to avoid confusion
        if (parsedMetadata.name) {
          delete parsedMetadata.name;
        }
        formData.append("metadata", JSON.stringify(parsedMetadata));
      } catch (error) {
        throw new Error("Invalid JSON in metadata field");
      }

      // Upload the file to create a new pipeline import
      const uploadResponse = await fetch(`${baseURL}pipeline-imports/${pipelineImport.workspace_id}/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw errorData.err || "Failed to rerun import";
      }

      // Optional: Update the original pipeline import status to "rerun"
      // This would require backend support to add "rerun" status to pipeline imports

      notification({
        title: "Success",
        message: "Import rerun initiated successfully",
        type: "success",
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error rerunning import:", error);
      notification({
        title: "Error",
        message: error instanceof Error ? error.message : String(error),
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to generate incremented name
  const generateIncrementedName = (originalName: string): string => {
    // Check if name already ends with a number in parentheses like "Name (2)"
    const match = originalName.match(/^(.*)\s\((\d+)\)$/);

    if (match) {
      // Extract base name and current number
      const baseName = match[1];
      const currentNumber = parseInt(match[2], 10);
      // Increment the number
      return `${baseName} (${currentNumber + 1})`;
    } else {
      // If no number in parentheses, add (2)
      return `${originalName} (2)`;
    }
  };

  // Helper function to get file extension from content type
  const getExtensionFromContentType = (contentType: string): string => {
    switch (contentType) {
      case "text/csv":
        return ".csv";
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        return ".xlsx";
      default:
        return "";
    }
  };

  return (
    <div className={style.container}>
      <div className={style.title}>
        <h2>Rerun Import</h2>
      </div>
      <p>A new import will be created from the original file and metadata.</p>

      <Box sx={styles.fieldContainer}>
        <Box sx={styles.schemaName}>
          <strong>Schema:</strong> {pipelineImport?.schema?.name || "Unknown Schema"}
        </Box>
      </Box>

      <Box sx={styles.fieldContainer}>
        <Input type="text" label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} disabled={isLoading} />
      </Box>

      <Box sx={styles.metadataLabel}>Metadata:</Box>
      <textarea
        value={metadata}
        onChange={(e) => setMetadata(e.target.value)}
        style={styles.metadataTextarea as React.CSSProperties}
        disabled={isLoading}
      />

      <fieldset disabled={isLoading}>
        <Box sx={styles.actionButtons}>
          <Button type="button" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variants={["primary"]} type="button" onClick={handleRerun} disabled={isLoading || !newName.trim()}>
            {isLoading ? "Processing..." : "Rerun"}
          </Button>
        </Box>
      </fieldset>
    </div>
  );
}
